// @ts-ignore
import api from "core/api/api";
import { checkSourcePrivileges } from "src/modules/wizards/services";
import { getParams } from "src/modules/wizards/services/wizards-methods-helper";
import { DB_PROVIDERS } from "app/components/appwizard/component-wizards/wizards/source/database-reader/configs/db-providers";
import * as PROVIDERS from "app/components/appwizard/component-wizards/wizards/source/database-reader/configs/providers";
import type { GenerateValidationListProps } from "../validation-steps.types";
import { TABLE_WRAPPER_API } from "app/components/appwizard/component-wizards/wizards/source/database-reader/models";
import type { Schema } from "src/modules/flow-designer/vts/select-tables-modal/schema-table";

const getValidationConfig = async (
    props: GenerateValidationListProps
) => {
    const type = (props.type ?? "").toUpperCase();

    return {
        type,
        wizardType:
            (PROVIDERS as Record<string, any>)[type]?.wizardType ?? type,
        apiParams: await getParams(type, props.model),
        agentName: props.agentName ?? null,
    };
};

const prepareSchemaCollection = (
    schemas: string[],
    setState: (patch: any) => void
) => {
    const formattedSchemas: Schema[] = schemas.map((name, index) => ({
        id: index,
        name,
        isSelected: false,
        isDisabled: false,
        tables: [],
    }));

    setState((prev: any) => ({ ...prev, schemas: formattedSchemas }));
};

export const ReactValidationMethods = {
    async ValidateConnection(
        props: GenerateValidationListProps
    ) {
        const { wizardType, apiParams, agentName } =
            await getValidationConfig(props);

        return api.WizardHelper_ValidateConnection(
            wizardType,
            apiParams,
            agentName,
            null,
            null
        );
    },

    async GetSchemas(
        props: GenerateValidationListProps
    ) {
        try {
            const { type, agentName } = await getValidationConfig(props);

            const config = (PROVIDERS as Record<string, any>)[type];
            const metaObjectFQN = props.model?.get?.("objectID") ?? null;
            const adapterName_version = props.model?.get?.("sourceAdapter") ?? null;

            // Provider-specific override (return undefined to fall through)
            if (config?.fetchTableWrapper) {
                const result = await config.fetchTableWrapper(props.model, agentName, metaObjectFQN, adapterName_version, {
                    isCDC: props.isCDC
                });
                if (result !== undefined) {
                    if (result.result) {
                        if (props.setState) prepareSchemaCollection(result.message, props.setState);
                        return { result: true };
                    }
                    throw new Error("Could not get all the Schemas in the database");
                }
            }

            // Generic path: tableWrapper drives which API to call
            const provider = DB_PROVIDERS[type];
            const wizardType = config?.wizardType ?? type;
            const params = config?.getConnectionParams
                ? await config.getConnectionParams(type, props.model)
                : await getParams(type, props.model);

            const apiFn = TABLE_WRAPPER_API[provider?.tableWrapper];
            if (!apiFn) {
                throw new Error(
                    `No table wrapper fetch API for tableWrapper="${provider?.tableWrapper}" (type=${type})`
                );
            }

            const response = await apiFn(wizardType, params, agentName, metaObjectFQN, adapterName_version);

            if (response.result) {
                if (props.setState) prepareSchemaCollection(response.message, props.setState);
                return { result: true };
            }

            throw new Error("Could not get all the Schemas in the database");
        } catch (e) {
            throw e;
        }
    },

    CheckPrivileges(
        props: GenerateValidationListProps,
        wizardType: string
    ) {
        return checkSourcePrivileges(
            (props.type ?? "").toUpperCase(),
            props.model,
            wizardType
        );
    },

    async CheckCDCConfigurations(
        props: GenerateValidationListProps
    ) {
        const { wizardType, apiParams, agentName } =
            await getValidationConfig(props);

        return api.WizardHelper_CheckCDCConfigurations(
            wizardType,
            apiParams,
            agentName,
            null,
            null
        );
    },
};